import React from 'react';
import { 
  Network, 
  Settings, 
  Zap, 
  Code, 
  Globe, 
  Link,
  Clock,
  TrendingUp 
} from 'lucide-react';

const NetworkStatsCards = ({ networks = [], trackingMethods = [] }) => {
  // Calcular estatísticas
  const totalNetworks = networks.length;
  
  // Contar redes por método de rastreamento
  const methodCounts = trackingMethods.reduce((acc, method) => {
    acc[method.value] = networks.filter(network => network.tracking_method === method.value).length;
    return acc;
  }, {});

  // Redes modificadas recentemente (últimos 7 dias)
  const recentlyModified = networks.filter(network => {
    if (!network.modified_at) return false;
    const modifiedDate = new Date(network.modified_at);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return modifiedDate > weekAgo;
  }).length;

  // Identificar o método mais usado
  const mostUsedMethod = Object.entries(methodCounts).reduce((a, b) => 
    methodCounts[a[0]] > methodCounts[b[0]] ? a : b, 
    ['', 0]
  );

  // Configuração dos ícones por método de rastreamento
  const getMethodIcon = (method) => {
    switch (method) {
      case 'manual_builder':
        return <Settings className="h-4 w-4" />;
      case 'template_builder':
        return <Code className="h-4 w-4" />;
      case 'action_pay_builder':
      case 'afilio_builder':
      case 'clickwise_builder':
      case 'lomadee_builder':
        return <Zap className="h-4 w-4" />;
      default:
        return <Link className="h-4 w-4" />;
    }
  };

  // Configuração das cores por método
  const getMethodColor = (method) => {
    switch (method) {
      case 'manual_builder':
        return 'border-gray-500 text-gray-600';
      case 'template_builder':
        return 'border-blue-500 text-blue-600';
      case 'action_pay_builder':
        return 'border-green-500 text-green-600';
      case 'afilio_builder':
        return 'border-purple-500 text-purple-600';
      case 'clickwise_builder':
        return 'border-orange-500 text-orange-600';
      case 'lomadee_builder':
        return 'border-pink-500 text-pink-600';
      default:
        return 'border-indigo-500 text-indigo-600';
    }
  };

  const cards = [
    {
      title: "Total de Redes",
      value: totalNetworks,
      icon: <Network className="h-5 w-5" />,
      color: "border-blue-500 text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      title: "Método Mais Usado",
      value: mostUsedMethod[1] > 0 ? `${mostUsedMethod[1]}x` : "N/A",
      subtitle: mostUsedMethod[1] > 0 ? trackingMethods.find(m => m.value === mostUsedMethod[0])?.label : "",
      icon: getMethodIcon(mostUsedMethod[0]),
      color: getMethodColor(mostUsedMethod[0]),
      bgColor: "bg-green-50"
    },
    {
      title: "Modificadas Recentemente",
      value: recentlyModified,
      subtitle: "Últimos 7 dias",
      icon: <Clock className="h-5 w-5" />,
      color: "border-orange-500 text-orange-600",
      bgColor: "bg-orange-50"
    },
    {
      title: "APIs Externas",
      value: methodCounts.action_pay_builder + methodCounts.afilio_builder + methodCounts.clickwise_builder + methodCounts.lomadee_builder || 0,
      subtitle: "vs Templates",
      icon: <Globe className="h-5 w-5" />,
      color: "border-purple-500 text-purple-600",
      bgColor: "bg-purple-50"
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
      {cards.map((card, index) => (
        <div 
          key={index}
          className={`bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 ${card.color.split(' ')[0]} hover:shadow-lg transition-shadow duration-200`}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500 mb-1">
                {card.title}
              </p>
              <p className={`text-2xl font-bold ${card.color.split(' ')[1]}`}>
                {card.value}
              </p>
              {card.subtitle && (
                <p className="text-xs text-gray-400 mt-1">
                  {card.subtitle}
                </p>
              )}
            </div>
            <div className={`${card.bgColor} p-3 rounded-full ${card.color}`}>
              {card.icon}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Componente adicional para breakdown detalhado dos métodos (opcional)
export const NetworkMethodBreakdown = ({ networks = [], trackingMethods = [] }) => {
  const methodCounts = trackingMethods.reduce((acc, method) => {
    acc[method.value] = {
      count: networks.filter(network => network.tracking_method === method.value).length,
      label: method.label
    };
    return acc;
  }, {});

  return (
    <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-indigo-500 col-span-full">
      <h3 className="text-sm font-medium text-gray-500 mb-3">
        Distribuição por Método de Rastreamento
      </h3>
      <div className="space-y-2">
        {Object.entries(methodCounts).map(([method, data]) => {
          const percentage = networks.length > 0 ? (data.count / networks.length * 100).toFixed(1) : 0;
          return (
            <div key={method} className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${
                  method.includes('api') ? 'from-green-400 to-green-600' : 'from-blue-400 to-blue-600'
                }`}></div>
                <span className="text-sm text-gray-700">{data.label}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-900">{data.count}</span>
                <span className="text-xs text-gray-500">({percentage}%)</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NetworkStatsCards;